import React, { useState, useRef, useCallback, useEffect } from 'react';
import api from '../api/axios';
import { SignalBars, Battery } from '../components/HandheldSimulator';
import SearchableSelect from '../components/SearchableSelect';

// ─── Constants ────────────────────────────────────────────────────────────────
const SCREEN_W = 320;
const SCREEN_H = 618;
const DEVICE_W = 358;

const C = {
  screenBg:    '#0a0f1e',
  primary:     '#1565C0',
  primaryLight:'#1e88e5',
  accent:      '#00b0ff',
  green:       '#4caf50',
  orange:      '#ff9800',
  red:         '#f44336',
  yellow:      '#ffd600',
  text:        '#e8eaf6',
  textDim:     '#7986cb',
  surface:     '#0d1b33',
  border:      'rgba(100,120,200,0.18)',
  step_done:   '#4caf50',
  step_active: '#00b0ff',
  step_idle:   '#2a3a5a',
};

// ─── Steps ───────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'SESSION',  icon: '▶' },
  { id: 2, label: 'LOCATION', icon: '📍' },
  { id: 3, label: 'SCAN',     icon: '📶' },
  { id: 4, label: 'SAVE',     icon: '✓' },
];

const MODES = [
  { id: 'uhf_audit', label: 'UHF Audit',    icon: '📶', desc: 'Audit registered assets' },
  { id: 'uhf_raw',   label: 'UHF Raw',      icon: '📡', desc: 'Scan all raw tags'       },
  { id: 'qr',        label: 'QR / Barcode', icon: '▣',  desc: 'Scan barcodes / QR'      },
  { id: 'nfc',       label: 'NFC',          icon: '🔵', desc: 'Read NFC tags'           },
];

// ─── Audio ────────────────────────────────────────────────────────────────────
const beep = (f = 880, d = 80, v = 0.3) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination);
    osc.type = 'square'; osc.frequency.setValueAtTime(f, ctx.currentTime);
    g.gain.setValueAtTime(v, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + d / 1000);
    osc.start(); osc.stop(ctx.currentTime + d / 1000);
  } catch {}
};

// ─── Small helpers ────────────────────────────────────────────────────────────
const uid = () => `SID-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;
const fmtTime = d => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtDateTime = d => new Date(d).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });

// ─── Status bar ───────────────────────────────────────────────────────────────
const StatusBar = ({ signal, battery, time }) => (
  <div className="handheld-statusbar" style={{ height: 22, background: 'rgba(0,0,0,0.7)', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', flexShrink: 0 }}>
    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>{time}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <SignalBars strength={signal} color={C.accent} size={0.7} />
      <Battery pct={battery} />
    </div>
  </div>
);

// ─── App bar ──────────────────────────────────────────────────────────────────
const AppBar = ({ sessionId, deviceUser, onLogout }) => (
  <div style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
    padding: '8px 14px', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>📡</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>TAG MY ASSETS CX1500N</div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.65)' }}>RFID Asset Management</div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {deviceUser ? (
          <>
            <div style={{ fontSize: 9, color: C.yellow, fontWeight: 700 }}>{deviceUser.full_name || deviceUser.username}</div>
            <button onClick={onLogout} style={{ fontSize: 8, color:'rgba(255,255,255,0.6)',
              background:'none', border:'none', cursor:'pointer', padding:0, marginTop:1 }}>
              ⏏ Logout
            </button>
          </>
        ) : sessionId ? (
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.5)' }}>SESSION</div>
            <div style={{ fontSize: 9, color: C.yellow, fontFamily: 'monospace', fontWeight: 700 }}>{sessionId}</div>
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepBar = ({ current }) => (
  <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.5)',
    padding: '6px 10px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
    {STEPS.map((s, i) => (
      <React.Fragment key={s.id}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 10, fontWeight: 700,
            background: current > s.id ? C.step_done : current === s.id ? C.step_active : C.step_idle,
            color: current >= s.id ? '#fff' : '#3a4a6a',
            boxShadow: current === s.id ? `0 0 8px ${C.accent}` : 'none',
            transition: 'all 0.3s',
          }}>
            {current > s.id ? '✓' : s.id}
          </div>
          <div style={{ fontSize: 7, marginTop: 2, letterSpacing: 0.4, fontWeight: current === s.id ? 700 : 400,
            color: current === s.id ? C.accent : current > s.id ? C.step_done : '#3a4a6a' }}>
            {s.label}
          </div>
        </div>
        {i < STEPS.length - 1 && (
          <div style={{ flex: 0.5, height: 1.5, background: current > s.id ? C.step_done : C.step_idle,
            transition: 'background 0.3s', marginBottom: 12 }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ─── Scan row (tag item in log) ───────────────────────────────────────────────
const ScanRow = ({ entry }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start', padding: '3px 0',
    borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
    <span style={{ fontSize: 9, color: '#3a4a6a', flexShrink: 0, marginTop: 1 }}>{fmtTime(entry.ts)}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, color: entry.status === 'ok' ? C.green : entry.status === 'unknown' ? C.orange : C.red,
        fontFamily: 'monospace', wordBreak: 'break-all' }}>{entry.tag}</div>
      {entry.name && <div style={{ fontSize: 9, color: C.textDim }}>{entry.name}</div>}
    </div>
    <div style={{ fontSize: 9, flexShrink: 0, fontWeight: 700,
      color: entry.status === 'ok' ? C.green : entry.status === 'unknown' ? C.orange : C.red }}>
      {entry.status === 'ok' ? '✓' : entry.status === 'unknown' ? '?' : '✗'}
    </div>
  </div>
);

// ─── Progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ value, total, color = C.green }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: C.textDim, marginBottom: 3 }}>
        <span>Progress</span><span style={{ color }}>{value} / {total} ({pct}%)</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color,
          borderRadius: 3, transition: 'width 0.4s', boxShadow: `0 0 6px ${color}88` }} />
      </div>
    </div>
  );
};

// ─── QR viewfinder ────────────────────────────────────────────────────────────
const QRViewfinder = ({ active, result }) => {
  const [y, setY] = useState(0);
  useEffect(() => {
    if (!active) return;
    let pos = 0; let dir = 1;
    const iv = setInterval(() => { pos += dir * 3; if (pos >= 100 || pos <= 0) dir *= -1; setY(pos); }, 25);
    return () => clearInterval(iv);
  }, [active]);
  return (
    <div style={{ height: 130, background: '#000', borderRadius: 8, position: 'relative',
      overflow: 'hidden', border: `1px solid ${active ? C.accent : C.border}` }}>
      {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
        <div key={v+h} style={{ position:'absolute', [v]:8, [h]:8, width:16, height:16,
          borderTop: v==='top' ? `2px solid ${active ? C.accent : '#333'}` : 'none',
          borderBottom: v==='bottom' ? `2px solid ${active ? C.accent : '#333'}` : 'none',
          borderLeft: h==='left' ? `2px solid ${active ? C.accent : '#333'}` : 'none',
          borderRight: h==='right' ? `2px solid ${active ? C.accent : '#333'}` : 'none' }} />
      ))}
      {active && <div style={{ position:'absolute', left:8, right:8, top:`${y}%`, height:2,
        background:`linear-gradient(90deg,transparent,${C.accent},transparent)`,
        boxShadow:`0 0 6px ${C.accent}` }} />}
      {!active && !result && <div style={{ position:'absolute', inset:0, display:'flex',
        alignItems:'center', justifyContent:'center', color:'#333', fontSize:11 }}>
        Waiting for scanner input…</div>}
      {result && <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center', background:'rgba(76,175,80,0.1)' }}>
        <div style={{ fontSize:20, color:C.green }}>✓</div>
        <div style={{ fontSize:10, color:C.green, fontWeight:700, marginTop:2 }}>CAPTURED</div>
        <div style={{ fontSize:9, color:C.accent, marginTop:2, maxWidth:200, textAlign:'center', wordBreak:'break-all' }}>{result.value}</div>
      </div>}
    </div>
  );
};

// ─── NFC zone ─────────────────────────────────────────────────────────────────
const NFCZone = ({ active, result }) => {
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (!active) return;
    let v = 0; const iv = setInterval(() => { v = (v+1)%4; setPulse(v); }, 380);
    return () => clearInterval(iv);
  }, [active]);
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'10px 0' }}>
      <div style={{ width:80, height:80, borderRadius:'50%', position:'relative',
        display:'flex', alignItems:'center', justifyContent:'center',
        background: result ? 'rgba(76,175,80,0.1)' : 'rgba(0,176,255,0.06)',
        border:`2px solid ${result ? C.green : active ? C.accent : C.border}`,
        boxShadow: active ? `0 0 18px ${C.accent}44` : 'none', transition:'all 0.3s' }}>
        {[1,2,3].map(r => (
          <div key={r} style={{ position:'absolute', borderRadius:'50%',
            border:`1px solid ${active && pulse >= r ? C.accent : 'rgba(100,120,200,0.12)'}`,
            width:26+r*16, height:26+r*16, transition:'border-color 0.3s' }} />
        ))}
        <div style={{ fontSize:22, zIndex:1 }}>{result ? '✓' : '📡'}</div>
      </div>
      <div style={{ fontSize:10, color: active ? C.accent : C.textDim, textAlign:'center' }}>
        {active ? 'Waiting for NFC tag…' : result ? 'Tag detected' : 'Tap NFC tag with the reader to capture'}
      </div>
      {result && (
        <div style={{ background:C.surface, borderRadius:6, padding:'6px 10px',
          border:`1px solid ${C.border}`, width:'100%', fontSize:10 }}>
          <div style={{ color:C.textDim }}>UID: <span style={{ color:C.accent, fontFamily:'monospace' }}>{result.uid}</span></div>
          <div style={{ color:C.textDim, marginTop:2 }}>Tech: <span style={{ color:C.text }}>{result.tech}</span></div>
          {result.name && <div style={{ color:C.green, marginTop:2 }}>{result.name}</div>}
        </div>
      )}
    </div>
  );
};

// ─── Device login screen ──────────────────────────────────────────────────────
const DeviceLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) { setError('Enter credentials'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/auth/login', { username: username.trim(), password: password.trim() });
      beep(880, 100, 0.3);
      onLogin(res.data.user);
    } catch (e) {
      setError(e.response?.data?.error || 'Invalid credentials');
      beep(300, 200, 0.3);
    }
    setLoading(false);
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', padding:'20px 18px', gap:14 }}>
      {/* Logo */}
      <div style={{ textAlign:'center', marginBottom:4 }}>
        <div style={{ fontSize:36 }}>📡</div>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, letterSpacing:1, marginTop:4 }}>TAG MY ASSETS</div>
        <div style={{ fontSize:9, color:C.textDim, letterSpacing:0.5 }}>CX1500N · RFID Asset Management</div>
      </div>

      {/* Username */}
      <div style={{ width:'100%' }}>
        <div style={{ fontSize:9, color:C.textDim, letterSpacing:0.5, marginBottom:4 }}>USERNAME</div>
        <div style={{ display:'flex', alignItems:'center', background:C.surface,
          border:`1px solid ${error ? C.red : C.border}`, borderRadius:8, overflow:'hidden' }}>
          <span style={{ padding:'0 8px', fontSize:14, color:C.textDim }}>👤</span>
          <input value={username} onChange={e => { setUsername(e.target.value); setError(''); }}
            onKeyDown={e => e.key==='Enter' && handleLogin()}
            placeholder="Enter username"
            style={{ flex:1, background:'transparent', border:'none', outline:'none',
              color:C.text, fontSize:12, padding:'9px 6px' }} />
        </div>
      </div>

      {/* Password */}
      <div style={{ width:'100%' }}>
        <div style={{ fontSize:9, color:C.textDim, letterSpacing:0.5, marginBottom:4 }}>PASSWORD</div>
        <div style={{ display:'flex', alignItems:'center', background:C.surface,
          border:`1px solid ${error ? C.red : C.border}`, borderRadius:8, overflow:'hidden' }}>
          <span style={{ padding:'0 8px', fontSize:14, color:C.textDim }}>🔒</span>
          <input value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={e => e.key==='Enter' && handleLogin()}
            type={showPass ? 'text' : 'password'}
            placeholder="Enter password"
            style={{ flex:1, background:'transparent', border:'none', outline:'none',
              color:C.text, fontSize:12, padding:'9px 6px' }} />
          <button onClick={() => setShowPass(p => !p)}
            style={{ background:'none', border:'none', color:C.textDim, cursor:'pointer',
              padding:'0 8px', fontSize:12 }}>
            {showPass ? '🙈' : '👁'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ width:'100%', background:'rgba(244,67,54,0.1)', border:`1px solid ${C.red}44`,
          borderRadius:6, padding:'6px 10px', fontSize:10, color:C.red, textAlign:'center' }}>
          ⚠ {error}
        </div>
      )}

      {/* Login button */}
      <button onClick={handleLogin} disabled={loading} style={{
        width:'100%', padding:'11px',
        background: loading ? 'rgba(255,255,255,0.08)' : `linear-gradient(135deg,${C.primary},${C.primaryLight})`,
        color: loading ? C.textDim : '#fff',
        border:'none', borderRadius:8, fontSize:12, fontWeight:700,
        cursor: loading ? 'not-allowed' : 'pointer', letterSpacing:0.5,
        boxShadow: loading ? 'none' : '0 3px 12px rgba(21,101,192,0.4)',
      }}>
        {loading
          ? <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <span style={{ width:10,height:10,border:`1.5px solid ${C.textDim}`,
                borderTopColor:'transparent',borderRadius:'50%',display:'inline-block',
                animation:'spin 0.7s linear infinite' }} /> SIGNING IN…
            </span>
          : '🔐 SIGN IN'}
      </button>

      <div style={{ fontSize:9, color:'#2a3a5a', textAlign:'center', lineHeight:1.5 }}>
        Use your web credentials<br/>to access the handheld device
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const HandheldSimulatorPage = () => {
  // ── device auth ──
  const [deviceLoggedIn, setDeviceLoggedIn] = useState(false);
  const [deviceUser, setDeviceUser]         = useState(null);

  // ── session state ──
  const [step, setStep]               = useState(1);
  const [sessionId, setSessionId]     = useState(null);
  const [sessionStarted, setSessionStarted] = useState(null);
  const [mode, setMode]               = useState(null);          // uhf_audit | uhf_raw | qr | nfc
  const [locationId, setLocationId]   = useState('');
  const [locations, setLocations]     = useState([]);
  const [auditId, setAuditId]         = useState(null);
  const [rawSessionId, setRawSessionId] = useState(null);

  // ── scan state ──
  const [scanLog, setScanLog]         = useState([]);
  const [isScanning, setIsScanning]   = useState(false);
  const [scanCount, setScanCount]     = useState({ total: 0, scanned: 0, missing: 0, unknown: 0 });
  const [qrActive]                    = useState(false);
  const [qrResult, setQrResult]       = useState(null);
  const [nfcActive]                   = useState(false);
  const [nfcResult, setNfcResult]     = useState(null);

  // ── save state ──
  const [isSaving, setIsSaving]       = useState(false);
  const [saveResult, setSaveResult]   = useState(null); // { status, audit, summary }

  // ── device chrome ──
  const [signal, setSignal]           = useState(3);
  const [battery]                     = useState(74);
  const [time, setTime]               = useState('');
  const [ledColor, setLedColor]       = useState('#333');
  const [syncStatus, setSyncStatus]   = useState('idle'); // idle | syncing | synced | error

  // ── recent sessions (right panel) ──
  const [recentAudits, setRecentAudits] = useState([]);

  // ── scanner input (hardware scanner emulating keyboard) ──
  const [assetsCache, setAssetsCache] = useState([]);
  const [lastScanned, setLastScanned]  = useState(null);
  const scannerRef = useRef(null);
  const logRef = useRef(null);

  // ── clock ──
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }));
    tick(); const iv = setInterval(tick, 10000); return () => clearInterval(iv);
  }, []);

  // ── signal animation ──
  useEffect(() => {
    const iv = setInterval(() => setSignal(s => Math.max(1, Math.min(5, s + (Math.random()>0.5?1:-1)))), 900);
    return () => clearInterval(iv);
  }, []);

  // ── auto-scroll log ──
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [scanLog]);

  // ── fetch locations + recent audits ──
  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    refreshAudits();
  }, []);

  const refreshAudits = () => {
    api.get('/audit').then(r => setRecentAudits(r.data.slice(0, 6))).catch(() => {});
  };

  const handleDeviceLogin = (user) => {
    setDeviceUser(user);
    setDeviceLoggedIn(true);
    flashLed(C.green, 800);
  };

  const handleDeviceLogout = () => {
    setDeviceLoggedIn(false);
    setDeviceUser(null);
    setStep(1);
    setScanLog([]);
    setSessionId(null);
    setSaveResult(null);
  };

  // ── flash LED ──
  const flashLed = (color, duration = 600) => {
    setLedColor(color);
    setTimeout(() => setLedColor('#333'), duration);
  };

  // ── add to log ──
  const addLog = useCallback((tag, status = 'ok', name = '') => {
    setScanLog(p => [...p, { tag, status, name, ts: new Date().toISOString() }]);
    beep(status === 'ok' ? 880 : status === 'unknown' ? 660 : 440, 80);
    flashLed(status === 'ok' ? C.green : status === 'unknown' ? C.orange : C.red);
  }, []);

  // ── keep hidden scanner input focused while in scan step ──
  useEffect(() => {
    if (step === 3 && deviceLoggedIn && scannerRef.current) {
      scannerRef.current.focus();
    }
  }, [step, deviceLoggedIn, mode, scanLog.length]);

  // ── process a single scanned tag value (called on Enter from hidden input) ──
  const processScan = useCallback(async (rawValue) => {
    const value = (rawValue || '').trim();
    if (!value || isScanning || step !== 3) return;
    setIsScanning(true);
    setLastScanned(value);
    setSyncStatus('syncing');

    try {
      if (mode === 'uhf_audit' && auditId) {
        const res = await api.post(`/audit/${auditId}/scan`, { rfid_tag: value });
        const apiStatus = res.data.status;
        const status = apiStatus === 'scanned' ? 'ok'
          : apiStatus === 'already_scanned' ? 'ok'
          : 'unknown';
        // Find asset name from cache
        const asset = assetsCache.find(a => a.rfid_tag === value);
        const name = asset?.asset_name
          || res.data.asset_name
          || (apiStatus === 'already_scanned' ? 'Already scanned' : 'Unknown tag');
        addLog(value, status, name);
        setScanCount(p => ({
          total:   res.data.total_count   ?? p.total,
          scanned: res.data.scanned_count ?? p.scanned,
          missing: res.data.missing_count ?? p.missing,
          unknown: status === 'unknown' ? p.unknown + 1 : p.unknown,
        }));
        setSyncStatus('synced');
      } else if (mode === 'uhf_raw') {
        await api.post('/raw-inventory/scan', {
          tag_number: value, rssi: -65, session_id: rawSessionId
        });
        addLog(value, 'ok', '');
        setScanCount(p => ({ ...p, total: p.total + 1, scanned: p.scanned + 1 }));
        setSyncStatus('synced');
      } else if (mode === 'qr') {
        const asset = assetsCache.find(
          a => a.barcode === value || a.serial_number === value || a.rfid_tag === value
        );
        setQrResult({ value, asset });
        await api.post('/raw-inventory/scan', {
          tag_number: value, rssi: -65, session_id: rawSessionId
        });
        addLog(value, asset ? 'ok' : 'unknown', asset?.asset_name || 'Unregistered');
        setScanCount(p => ({ ...p, total: p.total + 1, scanned: p.scanned + 1 }));
        setSyncStatus('synced');
      } else if (mode === 'nfc') {
        const asset = assetsCache.find(
          a => a.rfid_tag === value || a.serial_number === value || a.barcode === value
        );
        setNfcResult({ uid: value, tech: 'ISO14443-A', name: asset?.asset_name });
        await api.post('/raw-inventory/scan', {
          tag_number: value, rssi: -55, session_id: rawSessionId
        });
        addLog(value, asset ? 'ok' : 'unknown', asset?.asset_name || 'Unknown tag');
        setScanCount(p => ({ ...p, total: p.total + 1, scanned: p.scanned + 1 }));
        setSyncStatus('synced');
      }
    } catch (e) {
      addLog(value, 'error', e.response?.data?.error || e.message);
      setSyncStatus('error');
    } finally {
      setIsScanning(false);
      // Refocus the hidden input for the next scan
      setTimeout(() => scannerRef.current?.focus(), 30);
    }
  }, [isScanning, step, mode, auditId, assetsCache, rawSessionId, addLog]);

  // ─── STEP 1 — Start Session ────────────────────────────────────────────────
  const handleStartSession = () => {
    const id = uid();
    setSessionId(id);
    setSessionStarted(new Date().toISOString());
    setScanLog([]);
    setScanCount({ total:0, scanned:0, missing:0, unknown:0 });
    setSaveResult(null);
    setAuditId(null);
    setRawSessionId(null);
    setQrResult(null);
    setNfcResult(null);
    setMode(null);
    setLocationId('');
    setStep(2);
    beep(660, 120, 0.3);
    flashLed(C.green, 800);
  };

  // ─── STEP 2 — Confirm mode + location ─────────────────────────────────────
  const handleConfirmSetup = async () => {
    if (!mode) { alert('Select a scan mode'); return; }
    if ((mode === 'uhf_audit') && !locationId) { alert('Select a location for Audit mode'); return; }

    setSyncStatus('syncing');
    try {
      if (mode === 'uhf_audit') {
        // Create audit on backend
        const res = await api.post('/audit/start', { location_id: locationId });
        setAuditId(res.data.id);
        setScanCount({ total: res.data.total_assets, scanned: 0,
          missing: res.data.total_assets, unknown: 0 });
        // Pre-cache assets in this location so we can show name on each scan
        try {
          const a = await api.get('/assets', { params: { location_id: locationId, limit: 500 } });
          setAssetsCache(a.data || []);
        } catch { setAssetsCache([]); }
      } else if (mode === 'uhf_raw' || mode === 'qr' || mode === 'nfc') {
        // Create raw inventory session on backend
        const res = await api.post('/raw-inventory/session/start');
        setRawSessionId(res.data.session_id);
        // Cache all assets so QR / NFC scans can show a friendly name
        try {
          const a = await api.get('/assets', { params: { limit: 500 } });
          setAssetsCache(a.data || []);
        } catch { setAssetsCache([]); }
      }
      setSyncStatus('synced');
      setStep(3);
      beep(880, 80, 0.25);
    } catch (err) {
      setSyncStatus('error');
      const msg = err.response?.data?.error || err.message;
      // If audit already exists, resume it
      if (err.response?.status === 409 && err.response?.data?.audit_id) {
        setAuditId(err.response.data.audit_id);
        const res = await api.get(`/audit/${err.response.data.audit_id}/items`);
        const scanned = res.data.filter(i => i.scanned).length;
        setScanCount({ total: res.data.length, scanned, missing: res.data.length - scanned, unknown: 0 });
        setSyncStatus('synced');
        setStep(3);
      } else {
        alert('Backend error: ' + msg);
      }
    }
  };

  // ─── STEP 4 — Save ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true); setSyncStatus('syncing');
    try {
      let result = null;
      if (mode === 'uhf_audit' && auditId) {
        const res = await api.post(`/audit/${auditId}/complete`);
        result = {
          type: 'audit',
          auditId: res.data.id,
          location: res.data.location_name,
          total: res.data.total_assets,
          scanned: res.data.scanned_assets,
          missing: res.data.missing_assets,
          date: res.data.audit_date,
        };
      } else {
        result = {
          type: 'raw',
          session: rawSessionId,
          total: scanCount.total,
          scanned: scanCount.scanned,
        };
      }
      setSaveResult(result);
      setSyncStatus('synced');
      setStep(4);
      beep(880, 100, 0.3);
      setTimeout(() => beep(1100, 100, 0.25), 150);
      flashLed(C.green, 1200);
      refreshAudits();
    } catch (e) {
      setSyncStatus('error');
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    }
    setIsSaving(false);
  };

  // ─── Screen content by step ───────────────────────────────────────────────
  const renderContent = () => {
    // ── Step 1: Start session ──
    if (step === 1) return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
        justifyContent:'center', gap:18, padding:'16px 20px' }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📡</div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, letterSpacing:0.5 }}>TAG MY ASSETS</div>
          <div style={{ fontSize:10, color:C.textDim }}>Asset Management</div>
          <div style={{ fontSize:9, color:C.textDim, marginTop:4 }}>
            {new Date().toLocaleDateString([], { weekday:'short', year:'numeric', month:'short', day:'2-digit' })}
          </div>
        </div>

        <button onClick={handleStartSession} style={{
          width:'80%', padding:'14px 0',
          background: `linear-gradient(135deg, ${C.green}, #388e3c)`,
          color:'#fff', border:'none', borderRadius:10, fontSize:13,
          fontWeight:700, letterSpacing:1, cursor:'pointer',
          boxShadow:`0 4px 16px rgba(76,175,80,0.4)`,
        }}>
          ▶ START SESSION
        </button>

        <div style={{ width:'100%', background:C.surface, borderRadius:8, padding:'10px 12px',
          border:`1px solid ${C.border}`, fontSize:10 }}>
          <div style={{ color:C.textDim, marginBottom:6, fontWeight:600 }}>Last session</div>
          {recentAudits.length > 0 ? (
            <div>
              <div style={{ color:C.text }}>{recentAudits[0].location_name}</div>
              <div style={{ color:C.textDim, marginTop:2 }}>{fmtDateTime(recentAudits[0].audit_date)}</div>
              <div style={{ color: recentAudits[0].status === 'completed' ? C.green : C.orange, marginTop:2, textTransform:'uppercase', fontSize:9 }}>
                {recentAudits[0].status}
              </div>
            </div>
          ) : <div style={{ color:'#2a3a5a' }}>No previous sessions</div>}
        </div>
      </div>
    );

    // ── Step 2: Select mode + location ──
    if (step === 2) return (
      <div style={{ flex:1, overflowY:'auto', padding:'12px 12px' }}>
        <div style={{ fontSize:10, color:C.accent, fontWeight:700, letterSpacing:0.5, marginBottom:8 }}>
          SELECT SCAN MODE
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:12 }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)} style={{
              padding:'8px 6px', border:`1.5px solid ${mode===m.id ? C.accent : C.border}`,
              background: mode===m.id ? `${C.accent}18` : 'rgba(255,255,255,0.03)',
              borderRadius:8, cursor:'pointer', textAlign:'left',
              boxShadow: mode===m.id ? `0 0 10px ${C.accent}33` : 'none',
            }}>
              <div style={{ fontSize:16, marginBottom:3 }}>{m.icon}</div>
              <div style={{ fontSize:10, fontWeight:700, color: mode===m.id ? C.accent : C.text }}>{m.label}</div>
              <div style={{ fontSize:8, color:C.textDim, marginTop:1 }}>{m.desc}</div>
            </button>
          ))}
        </div>

        {mode === 'uhf_audit' && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.accent, fontWeight:700, letterSpacing:0.5, marginBottom:6 }}>
              SELECT LOCATION
            </div>
            <SearchableSelect value={locationId} onChange={e => setLocationId(e.target.value)} style={{
              width:'100%', background:C.surface, color:C.text,
              border:`1px solid ${locationId ? C.accent : C.border}`, borderRadius:6,
              padding:'7px 8px', fontSize:11, outline:'none',
            }}>
              <option value="">-- SELECT --</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.location_code} — {l.location_name}</option>
              ))}
            </SearchableSelect>
          </div>
        )}

        <div style={{ background:C.surface, borderRadius:6, padding:'7px 10px',
          border:`1px solid ${C.border}`, fontSize:9, color:C.textDim, marginBottom:12 }}>
          <div style={{ color:C.yellow, fontWeight:700, marginBottom:3 }}>SESSION</div>
          <div style={{ fontFamily:'monospace', color:C.text }}>{sessionId}</div>
          <div style={{ marginTop:2 }}>Started: {sessionStarted ? fmtDateTime(sessionStarted) : '—'}</div>
        </div>

        <button onClick={handleConfirmSetup}
          disabled={!mode || (mode==='uhf_audit' && !locationId)}
          style={{
            width:'100%', padding:'10px', borderRadius:8, border:'none', fontSize:12,
            fontWeight:700, letterSpacing:0.5, cursor: (!mode||(mode==='uhf_audit'&&!locationId)) ? 'not-allowed':'pointer',
            background: (!mode||(mode==='uhf_audit'&&!locationId))
              ? 'rgba(255,255,255,0.08)'
              : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            color: (!mode||(mode==='uhf_audit'&&!locationId)) ? C.textDim : '#fff',
            boxShadow: (!mode||(mode==='uhf_audit'&&!locationId)) ? 'none' : '0 3px 12px rgba(21,101,192,0.4)',
          }}>
          CONFIRM & PROCEED →
        </button>
      </div>
    );

    // ── Step 3: Scan ──
    if (step === 3) return (
      <div
        style={{ flex:1, display:'flex', flexDirection:'column', gap:8, overflowY:'auto', padding:'10px 12px' }}
        onClick={() => scannerRef.current?.focus()}
      >
        {/* Hidden scanner input — receives keyboard emulation from hardware scanner.
            On Enter, the value is processed as a scan and the input clears for the next tag. */}
        <input
          ref={scannerRef}
          type="text"
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="none"
          aria-hidden="true"
          tabIndex={-1}
          onBlur={() => { setTimeout(() => scannerRef.current?.focus(), 50); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const v = e.currentTarget.value;
              e.currentTarget.value = '';
              processScan(v);
            }
          }}
          style={{
            position:'absolute', left:-9999, top:-9999,
            width:1, height:1, opacity:0, border:'none', padding:0, background:'transparent',
          }}
        />

        {/* Mode + sync badge */}
        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
          <div style={{ flex:1, background:C.surface, borderRadius:6, padding:'5px 8px',
            border:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:7, height:7, borderRadius:'50%',
              background: isScanning ? C.green : '#444',
              boxShadow: isScanning ? `0 0 8px ${C.green}` : 'none', transition:'all 0.3s' }} />
            <span style={{ fontSize:9, color:C.textDim }}>{MODES.find(m=>m.id===mode)?.label}</span>
            {mode === 'uhf_audit' && <span style={{ fontSize:8, color:C.accent, marginLeft:'auto' }}>902–928 MHz</span>}
            <SignalBars strength={signal} color={C.accent} size={0.65} />
          </div>
          <div style={{ background:C.surface, borderRadius:6, padding:'5px 8px',
            border:`1px solid ${syncStatus==='synced'?C.green:syncStatus==='syncing'?C.yellow:syncStatus==='error'?C.red:C.border}`,
            fontSize:8, fontWeight:700,
            color:syncStatus==='synced'?C.green:syncStatus==='syncing'?C.yellow:syncStatus==='error'?C.red:C.textDim,
            display:'flex', alignItems:'center', gap:4 }}>
            {syncStatus==='syncing' && <span style={{ width:7,height:7,border:`1.5px solid ${C.yellow}`,
              borderTopColor:'transparent',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite' }} />}
            {syncStatus === 'synced'  ? '✓ SYNCED' :
             syncStatus === 'syncing' ? 'SYNC…'    :
             syncStatus === 'error'   ? '✗ ERR'    : 'READY'}
          </div>
        </div>

        {/* Audit progress */}
        {mode === 'uhf_audit' && (
          <div style={{ background:C.surface, borderRadius:6, padding:'8px 10px',
            border:`1px solid ${C.border}`, flexShrink:0 }}>
            <ProgressBar value={scanCount.scanned} total={scanCount.total} />
            <div style={{ display:'flex', gap:10, marginTop:6, fontSize:9 }}>
              <span style={{ color:C.green }}>✓ {scanCount.scanned} Scanned</span>
              <span style={{ color:C.red }}>✗ {scanCount.missing} Missing</span>
              <span style={{ color:C.orange, marginLeft:'auto' }}>? {scanCount.unknown} Unknown</span>
            </div>
          </div>
        )}

        {/* QR viewfinder */}
        {mode === 'qr' && <QRViewfinder active={qrActive} result={qrResult} />}

        {/* NFC zone */}
        {mode === 'nfc' && <NFCZone active={nfcActive} result={nfcResult} />}

        {/* Scan log */}
        <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:9,
            color:C.textDim, marginBottom:4, letterSpacing:0.4 }}>
            <span>SCAN LOG</span>
            <span>{scanLog.length} scans</span>
          </div>
          <div ref={logRef} style={{ height: mode==='uhf_audit' ? 130 : mode==='qr'||mode==='nfc' ? 80 : 160,
            overflowY:'auto', background:'rgba(0,0,0,0.5)', borderRadius:6,
            padding:'6px 8px', border:`1px solid ${C.border}` }}>
            {scanLog.length === 0
              ? <div style={{ color:'#2a3a5a', textAlign:'center', paddingTop:24, fontSize:11, lineHeight:1.6 }}>
                  Waiting for scanner…<br/>
                  <span style={{ fontSize:9, color:'#1a2540' }}>Scan a tag — value + Enter is processed automatically.</span>
                </div>
              : scanLog.map((e,i) => <ScanRow key={i} entry={e} />)
            }
          </div>
        </div>

        {/* Sync + Save buttons */}
        {scanLog.length > 0 && !isScanning && (
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            <button onClick={() => {
              setSyncStatus('syncing');
              if (mode === 'uhf_audit' && auditId) {
                api.get(`/audit/${auditId}/items`).then(() => {
                  setSyncStatus('synced'); refreshAudits(); addLog('Synced with server ✓', 'ok');
                }).catch(() => { setSyncStatus('error'); addLog('Sync failed', 'error'); });
              } else { setSyncStatus('synced'); addLog('Synced with server ✓', 'ok'); }
            }} style={{
              padding:'8px 12px',
              background: syncStatus === 'syncing' ? 'rgba(255,152,0,0.14)' : 'rgba(33,150,243,0.12)',
              color: syncStatus === 'syncing' ? '#FFB74D' : C.accent,
              border:`1px solid ${syncStatus==='syncing'?'rgba(255,183,77,0.3)':C.border}`,
              borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer', letterSpacing:0.4,
            }}>
              {syncStatus === 'syncing' ? '⟳ …' : '⟳ SYNC'}
            </button>
            <button onClick={handleSave} disabled={isSaving} style={{
              flex:1, padding:'8px',
              background: isSaving ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg,#0d47a1,#1565C0)`,
              color: isSaving ? C.textDim : '#fff',
              border:`1px solid ${C.border}`, borderRadius:8, fontSize:11,
              fontWeight:700, cursor: isSaving?'not-allowed':'pointer', letterSpacing:0.5,
            }}>
              {isSaving ? '⟳ SAVING…' : '💾 SAVE & SYNC →'}
            </button>
          </div>
        )}
      </div>
    );

    // ── Step 4: Saved ──
    if (step === 4 && saveResult) return (
      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
        justifyContent:'center', gap:14, padding:'16px 14px' }}>
        <div style={{ width:60, height:60, borderRadius:'50%',
          background:'rgba(76,175,80,0.15)', border:`2px solid ${C.green}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:28, boxShadow:`0 0 20px ${C.green}44` }}>✓</div>

        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.green }}>SESSION SAVED</div>
          <div style={{ fontSize:9, color:C.textDim, marginTop:3 }}>Synced to backend ✓</div>
        </div>

        <div style={{ width:'100%', background:C.surface, borderRadius:8, padding:'10px 12px',
          border:`1px solid ${C.green}44`, fontSize:10 }}>
          {saveResult.type === 'audit' ? (
            <>
              <div style={{ color:C.textDim, marginBottom:4 }}>Audit #{saveResult.auditId}</div>
              <div style={{ color:C.text, fontWeight:600, marginBottom:6 }}>{saveResult.location}</div>
              <div style={{ display:'flex', gap:12 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{saveResult.total}</div>
                  <div style={{ fontSize:8, color:C.textDim }}>TOTAL</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:C.green }}>{saveResult.scanned}</div>
                  <div style={{ fontSize:8, color:C.textDim }}>FOUND</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:700, color:C.red }}>{saveResult.missing}</div>
                  <div style={{ fontSize:8, color:C.textDim }}>MISSING</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ color:C.textDim, marginBottom:4 }}>Raw Inventory Session</div>
              <div style={{ fontSize:8, color:C.textDim, fontFamily:'monospace', wordBreak:'break-all', marginBottom:6 }}>{saveResult.session}</div>
              <div style={{ color:C.green, fontWeight:600 }}>{saveResult.scanned} tags recorded</div>
            </>
          )}
        </div>

        <button onClick={handleStartSession} style={{
          width:'80%', padding:'11px 0',
          background:`linear-gradient(135deg,${C.green},#388e3c)`,
          color:'#fff', border:'none', borderRadius:10, fontSize:12,
          fontWeight:700, letterSpacing:1, cursor:'pointer',
          boxShadow:`0 4px 14px rgba(76,175,80,0.4)`,
        }}>▶ NEW SESSION</button>

        <div style={{ fontSize:9, color:C.textDim, textAlign:'center' }}>
          View results in Audit Report or Raw Inventory
        </div>
      </div>
    );
  };

  // ─── Physical device ──────────────────────────────────────────────────────
  return (
    <div className="handheld-page" style={{ minHeight:'100vh',
      background:'linear-gradient(135deg,#0d0d1a 0%,#0a1628 50%,#0d0d1a 100%)',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start',
      padding:'28px 16px 40px', fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

      <div className="handheld-title" style={{ marginBottom:24, textAlign:'center' }}>
        <div style={{ fontSize:20, fontWeight:700, color:'#fff', letterSpacing:0.5 }}>Handheld Simulator</div>
        <div style={{ fontSize:12, color:'#546e7a', marginTop:4 }}>CX1500N · 5.72" · UHF RFID + QR + NFC · Session Workflow</div>
      </div>

      <div className="handheld-flex" style={{ display:'flex', gap:32, alignItems:'flex-start', flexWrap:'wrap', justifyContent:'center' }}>

        {/* ── Device shell ── */}
        <div className="handheld-device-wrapper" style={{ position:'relative', userSelect:'none' }}>
          <div className="handheld-device" style={{ width:DEVICE_W, height:760,
            background:'linear-gradient(160deg,#2a2a2a 0%,#1a1a1a 40%,#111 100%)',
            borderRadius:28, position:'relative',
            boxShadow:'0 24px 64px rgba(0,0,0,0.7),inset 0 1px 0 rgba(255,255,255,0.06),0 0 0 1px #090909',
            display:'flex', flexDirection:'column', alignItems:'center' }}>

            {/* Top strip */}
            <div className="handheld-deco" style={{ width:'80%', height:36, marginTop:14,
              display:'flex', alignItems:'center', justifyContent:'space-between', paddingInline:8 }}>
              <div style={{ width:10,height:10,borderRadius:'50%',background:'#1a1a2e',
                border:'1.5px solid #2a2a3e',boxShadow:'inset 0 0 4px rgba(0,150,255,0.3)' }} />
              <div style={{ display:'flex',gap:3 }}>
                {Array.from({length:6}).map((_,i)=>(
                  <div key={i} style={{ width:3,height:10,background:'#111',borderRadius:2 }} />
                ))}
              </div>
              <div style={{ width:8,height:8,borderRadius:'50%',background:ledColor,
                boxShadow:ledColor!=='#333'?`0 0 10px ${ledColor}`:'none',transition:'all 0.2s' }} />
            </div>

            {/* Screen bezel */}
            <div className="handheld-bezel" style={{ width:SCREEN_W+8, height:SCREEN_H+8, background:'#050508',
              borderRadius:16, padding:4,
              boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.04)' }}>
              <div className="handheld-screen" style={{ width:SCREEN_W, height:SCREEN_H, background:C.screenBg,
                borderRadius:13, overflow:'hidden', display:'flex', flexDirection:'column' }}>
                <StatusBar signal={signal} battery={battery} time={time} />
                <AppBar
                  sessionId={step>1 ? sessionId : null}
                  deviceUser={deviceLoggedIn ? deviceUser : null}
                  onLogout={handleDeviceLogout}
                />
                {deviceLoggedIn && <StepBar current={step} />}
                <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>
                  {deviceLoggedIn ? renderContent() : <DeviceLogin onLogin={handleDeviceLogin} />}
                </div>
                {/* Bottom strip */}
                <div className={`handheld-bottom-strip ${!deviceLoggedIn ? 'handheld-bottom-login' : ''}`} style={{ padding:'6px 10px 8px', background:'rgba(0,0,0,0.5)',
                  borderTop:`1px solid ${C.border}`, display:'flex', gap:6, flexShrink:0 }}>
                  {!deviceLoggedIn ? (
                    <button onClick={() => document.querySelector('input')?.focus()} style={{
                      flex:1, padding:'7px', background:`linear-gradient(135deg,${C.primary},${C.primaryLight})`,
                      color:'#fff', border:'none', borderRadius:6, fontSize:10,
                      fontWeight:700, cursor:'pointer',
                    }}>🔐 SIGN IN TO CONTINUE</button>
                  ) : step === 1 ? (
                    <button onClick={handleStartSession} style={{
                      flex:1, padding:'7px', background:`linear-gradient(135deg,${C.green},#388e3c)`,
                      color:'#fff', border:'none', borderRadius:6, fontSize:11,
                      fontWeight:700, cursor:'pointer', letterSpacing:0.5,
                    }}>▶ START</button>
                  ) : step === 2 ? (
                    <>
                      <button onClick={() => setStep(1)} style={{
                        flex:1, padding:'7px', background:'rgba(255,255,255,0.05)',
                        color:C.textDim, border:`1px solid ${C.border}`, borderRadius:6,
                        fontSize:10, cursor:'pointer',
                      }}>← BACK</button>
                      <button onClick={handleConfirmSetup}
                        disabled={!mode||(mode==='uhf_audit'&&!locationId)} style={{
                          flex:2, padding:'7px',
                          background:(!mode||(mode==='uhf_audit'&&!locationId))
                            ?'rgba(255,255,255,0.06)'
                            :`linear-gradient(135deg,${C.primary},${C.primaryLight})`,
                          color:(!mode||(mode==='uhf_audit'&&!locationId))?C.textDim:'#fff',
                          border:'none', borderRadius:6, fontSize:10,
                          fontWeight:700, cursor:(!mode||(mode==='uhf_audit'&&!locationId))?'not-allowed':'pointer',
                        }}>CONFIRM →</button>
                    </>
                  ) : step === 3 ? (
                    <>
                      <button onClick={() => { if(!isScanning){setStep(2);} }} style={{
                        flex:1, padding:'7px', background:'rgba(255,255,255,0.05)',
                        color:C.textDim, border:`1px solid ${C.border}`, borderRadius:6,
                        fontSize:10, cursor: isScanning?'not-allowed':'pointer',
                      }}>← BACK</button>
                      {isScanning ? (
                        <div style={{ flex:2, display:'flex', alignItems:'center',
                          justifyContent:'center', gap:6, background:'rgba(76,175,80,0.1)',
                          borderRadius:6, border:`1px solid ${C.green}44`, color:C.green,
                          fontSize:10, fontWeight:700 }}>
                          <span style={{ width:9,height:9,border:`1.5px solid ${C.green}`,
                            borderTopColor:'transparent',borderRadius:'50%',
                            display:'inline-block',animation:'spin 0.7s linear infinite' }} />
                          PROCESSING…
                        </div>
                      ) : (
                        <div
                          onClick={() => scannerRef.current?.focus()}
                          title="Scanner is listening — scan a tag to add"
                          style={{
                            flex:2, display:'flex', alignItems:'center', justifyContent:'center',
                            gap:6, background:'rgba(0,176,255,0.08)',
                            borderRadius:6, border:`1px solid ${C.accent}44`,
                            color:C.accent, fontSize:10, fontWeight:700, letterSpacing:0.5,
                            cursor:'text', padding:'7px',
                          }}>
                          <span style={{ width:7, height:7, borderRadius:'50%',
                            background:C.green, boxShadow:`0 0 6px ${C.green}` }} />
                          {lastScanned
                            ? <>READY · last: <span style={{ fontFamily:'monospace', color:C.text, marginLeft:4 }}>{lastScanned.length > 14 ? lastScanned.slice(0,14)+'…' : lastScanned}</span></>
                            : 'READY · waiting for scan'}
                        </div>
                      )}
                    </>
                  ) : (
                    <button onClick={handleStartSession} style={{
                      flex:1, padding:'7px',
                      background:`linear-gradient(135deg,${C.green},#388e3c)`,
                      color:'#fff', border:'none', borderRadius:6, fontSize:11,
                      fontWeight:700, cursor:'pointer', letterSpacing:0.5,
                    }}>▶ NEW SESSION</button>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom branding + home bar */}
            <div style={{ flex:1, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center', gap:6 }}>
              <div style={{ fontSize:9, color:'#2a2a3a', letterSpacing:2, fontWeight:700, marginTop:4 }}>CX1500N</div>
              <div style={{ width:80, height:4, background:'#2a2a2a', borderRadius:2 }} />
            </div>

            {/* Right: orange SCAN trigger (decorative — real scans come from the hardware scanner) */}
            <div className="handheld-deco" title="Hardware Trigger" style={{
              position:'absolute', right:-14, top:'52%', transform:'translateY(-50%)',
              width:14, height:72,
              background: isScanning
                ? 'linear-gradient(180deg,#1b5e20,#2e7d32)'
                : 'linear-gradient(180deg,#e65100,#f57c00)',
              borderRadius:'0 8px 8px 0',
              boxShadow: isScanning ? '3px 0 12px rgba(46,125,50,0.5)' : '3px 0 12px rgba(245,124,0,0.5)',
              cursor: 'default',
              display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
            }}>
              <div style={{ fontSize:7, color:'rgba(255,255,255,0.8)', writingMode:'vertical-rl',
                letterSpacing:1, fontWeight:700 }}>SCAN</div>
            </div>

            {/* Left: volume + power */}
            <div className="handheld-deco" style={{ position:'absolute', left:-10, top:'28%',
              display:'flex', flexDirection:'column', gap:8 }}>
              {['VOL+','VOL−','PWR'].map(l => (
                <div key={l} style={{ width:10, height:l==='PWR'?28:22,
                  background:'linear-gradient(90deg,#1a1a1a,#2a2a2a)',
                  borderRadius:'4px 0 0 4px', boxShadow:'-2px 0 6px rgba(0,0,0,0.4)',
                  display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                  <div style={{ fontSize:5, color:'#444', writingMode:'vertical-rl' }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Bottom: USB-C */}
            <div className="handheld-deco" style={{ position:'absolute', bottom:18, display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:14,height:6,background:'#111',borderRadius:2,border:'1px solid #222' }} />
              <div style={{ width:28,height:4,background:'#1a1a1a',borderRadius:2,border:'1px solid #252525' }} />
              <div style={{ width:14,height:6,background:'#111',borderRadius:2,border:'1px solid #222' }} />
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="handheld-side" style={{ width:260, display:'flex', flexDirection:'column', gap:14 }}>

          {/* Current session card */}
          <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:12, padding:'14px 16px',
            border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#fff', marginBottom:10, letterSpacing:0.5 }}>
              📋 Current Session
            </div>
            {sessionId ? (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:11 }}>
                  <span style={{ color:'#546e7a' }}>Session ID</span>
                  <span style={{ color:C.yellow, fontFamily:'monospace', fontSize:10 }}>{sessionId}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:11 }}>
                  <span style={{ color:'#546e7a' }}>Started</span>
                  <span style={{ color:C.text }}>{fmtDateTime(sessionStarted)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:11 }}>
                  <span style={{ color:'#546e7a' }}>Mode</span>
                  <span style={{ color:C.accent }}>{MODES.find(m=>m.id===mode)?.label || '—'}</span>
                </div>
                {locationId && (
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:11 }}>
                    <span style={{ color:'#546e7a' }}>Location</span>
                    <span style={{ color:C.text }}>
                      {locations.find(l=>String(l.id)===String(locationId))?.location_name || '—'}
                    </span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:11 }}>
                  <span style={{ color:'#546e7a' }}>Step</span>
                  <span style={{ color:C.accent, fontWeight:700 }}>{STEPS[step-1]?.label}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11 }}>
                  <span style={{ color:'#546e7a' }}>Sync</span>
                  <span style={{ fontWeight:700,
                    color:syncStatus==='synced'?C.green:syncStatus==='syncing'?C.yellow:syncStatus==='error'?C.red:C.textDim }}>
                    {syncStatus.toUpperCase()}
                  </span>
                </div>
                {mode === 'uhf_audit' && step >= 3 && (
                  <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
                    <ProgressBar value={scanCount.scanned} total={scanCount.total} />
                    <div style={{ display:'flex', gap:8, marginTop:6, fontSize:10 }}>
                      <span style={{ color:C.green }}>✓ {scanCount.scanned}</span>
                      <span style={{ color:C.red }}>✗ {scanCount.missing}</span>
                      <span style={{ color:C.orange }}>? {scanCount.unknown}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color:'#2a3a5a', fontSize:11 }}>No active session. Press START SESSION on device.</div>
            )}
          </div>

          {/* Workflow guide */}
          <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:12, padding:'14px 16px',
            border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#fff', marginBottom:10, letterSpacing:0.5 }}>
              🕹 Session Workflow
            </div>
            {[
              { n:'1', label:'Start Session',    desc:'Tap START SESSION to create a new session with unique ID.' },
              { n:'2', label:'Select Mode',      desc:'Choose UHF Audit, Raw Scan, QR or NFC. Pick a location for Audit.' },
              { n:'3', label:'Scan Assets',      desc:'Use the hardware scanner. Each tag (value + Enter) is matched and sent to the backend live.' },
              { n:'4', label:'Save & Sync',      desc:'Tap SAVE & SYNC to complete the audit. Report appears in Audit Report page.' },
            ].map(s => (
              <div key={s.n} style={{ display:'flex', gap:10, marginBottom:10, alignItems:'flex-start' }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:
                  Number(s.n) < step ? C.step_done : Number(s.n) === step ? C.step_active : C.step_idle,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:10, fontWeight:700, color:'#fff', flexShrink:0, marginTop:1 }}>
                  {Number(s.n) < step ? '✓' : s.n}
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700,
                    color: Number(s.n) === step ? C.accent : Number(s.n) < step ? C.step_done : '#546e7a' }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize:10, color:'#546e7a', lineHeight:1.4, marginTop:1 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent audits */}
          <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:12, padding:'14px 16px',
            border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#fff', marginBottom:10, letterSpacing:0.5 }}>
              📊 Recent Audits
            </div>
            {recentAudits.length === 0
              ? <div style={{ color:'#2a3a5a', fontSize:11 }}>No audits yet</div>
              : recentAudits.map(a => (
                  <div key={a.id} style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'flex-start', marginBottom:8, paddingBottom:8,
                    borderBottom:`1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize:11, color:C.text, fontWeight:500 }}>{a.location_name}</div>
                      <div style={{ fontSize:9, color:'#546e7a', marginTop:1 }}>{fmtDateTime(a.audit_date)}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase',
                        color: a.status==='completed' ? C.green : C.orange }}>
                        {a.status}
                      </div>
                      <div style={{ fontSize:9, color:'#546e7a', marginTop:1 }}>
                        {a.scanned_assets}/{a.total_assets}
                      </div>
                    </div>
                  </div>
                ))
            }
          </div>

          {/* Device specs */}
          <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:12, padding:'14px 16px',
            border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#fff', marginBottom:8, letterSpacing:0.5 }}>
              📱 CX1500N
            </div>
            {[['Screen','5.72" · 1520×720'],['UHF','902–928 MHz · 8m range'],
              ['QR/Bar','1D / 2D Imager'],['NFC','ISO14443 · 13.56MHz'],
              ['Battery','6000 mAh · IP67']].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:10 }}>
                <span style={{ color:'#546e7a' }}>{k}</span>
                <span style={{ color:C.text }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default HandheldSimulatorPage;
